import { useContext, useState, useCallback } from "react";
import Card from "shared/components/Card";
import QlikObject from "shared/components/QlikObject";
import ExportDataModal from "shared/components/ExportDataModal";
import { ExportData } from "shared/qlik-api/qlikUtils";
import GlobalContext from "shared/GlobalContext";
import { dispatchTouchEvent, dispatchPointerEvent } from "shared/utils/utils";

export default function QlikCard({
  id,
  title,
  titleIcon,
  height = "300px",
  showMaximize = true,
  showExport = true,
  exportTitle = "EXCEL",
  exportText = "Exporting data...",
  exportCompletedText = "Export completed. Click here to download the data file.",
  closeBtnText = "Close",
  showToggleDataView = false,
  showHeader = true,
  // showAllowInteractionButton = false,
  onBeforeExportHandler = null,
  onAfterExportHandler = null,
  addClasses = "",
}) {
  const { app, onTouchDevice } = useContext(GlobalContext);
  const [showDataView, setShowDataView] = useState(false);
  const [showExportDataModal, setShowExportDataModal] = useState(false);
  const [exportLink, setExportLink] = useState(null);
  const [exportError, setExportError] = useState('');
  const [allowSelections, setAllowSelections] = useState(!onTouchDevice); // !showAllowInteractionButton && !onTouchDevice
  const actions = [];

  // On touch devices only
  const onTapChartHandler = useCallback(
    (e) => {
      setAllowSelections(!allowSelections);
      // dispatch event to start selections
      dispatchTouchEvent(e, "touchstart");
      // for some charts (BarChart, PieChart, etc)
      dispatchPointerEvent(e, "pointerdown");
      setTimeout(() => dispatchPointerEvent(e, "pointerup"), 150);
    },
    [allowSelections]
  );

  const onExportStartedHandler = useCallback(async (data) => {
    setExportLink(null);
    setExportError(null);    
    setShowExportDataModal(true);
    if(onBeforeExportHandler)
      await onBeforeExportHandler(data);
  }, 
  [setShowExportDataModal, onBeforeExportHandler]
);

const onExportFinishedHandler = useCallback(async (data) => {
  const { link, error } = data;
  setShowExportDataModal(true);
  setExportLink(link);
  setExportError(error);
  if(onAfterExportHandler)
    await onAfterExportHandler(data);
}, 
[setExportLink, setShowExportDataModal, onAfterExportHandler]
);

  // showAllowInteractionButton || onTouchDevice
  if (onTouchDevice) {
    actions.push({
      //title: "Interactivity",
      icon: "lui-fade-button__icon lui-icon lui-icon--touch",
      btnClasses: ({ maximized }) =>
        allowSelections || maximized ? "active bg-light" : "",
      action: () => setAllowSelections(!allowSelections),
    });
  }

  if (showToggleDataView) {
    actions.push({
      icon: "lui-fade-button__icon lui-icon lui-icon--table",
      btnClasses: showDataView ? "active bg-light" : "",
      action: () => setShowDataView(!showDataView),
    });
  }

  if (showExport) {
    actions.push({
      title: exportTitle,
      icon: "lui-fade-button__icon lui-icon lui-icon--download",
      action: () => ExportData({ 
        app, 
        id, 
        format: "OOXML", 
        state: "P", 
        onExportStart: onExportStartedHandler, 
        onExportFinish: onExportFinishedHandler 
      }),
    });
  }

  return (
    <>
      {showExportDataModal && 
        <ExportDataModal 
          title={exportTitle}
          text={exportText}
          exportCompletedText={exportCompletedText}
          exportError={exportError}
          link={exportLink}
          closeBtnText={closeBtnText}
          onCloseHandler={() => setShowExportDataModal(false)}
        />
      }
        
      <Card
        title={title}
        titleIcon={titleIcon}
        showMaximize={showMaximize}
        actions={actions}
        addClasses={addClasses}
        showHeader={showHeader}
      >
        {({ maximized }) => {
          return (
            <QlikObject
              id={id}
              height={height} //maximized ? bodyHeight : height
              maximized={maximized}
              showDataView={showDataView}
              disableInteractivity={!allowSelections && !maximized}
              onTapEvent={onTapChartHandler}
            />
          );
        }}
      </Card>
    </>
  );
}
